/**
 * Manual client creation.
 *
 * Also the second half of converting a sign-up request: the tray navigates
 * here with the visitor's data pre-filled and the request's identifier in the
 * navigation state. The request is marked converted only once the client
 * exists, so abandoning this form leaves it pending — nothing was created.
 * There is deliberately no second creation path for conversions.
 */

import { Create, RaRecord, SimpleForm, useDataProvider, useNotify, useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { MyFormContainer, MyToolbar } from '@/components/forms';
import { isAlreadyDecided } from '@/features/signupRequests/components/decision';
import { ClientFormFields } from './ClientForm';

interface ConversionState {
  signupRequestId?: number;
}

export const ClientCreate = () => {
  const location = useLocation();
  const signupRequestId = (location.state as ConversionState | null)?.signupRequestId;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  const finishConversion = async (created: RaRecord) => {
    try {
      await dataProvider.update('signupRequests', {
        id: signupRequestId,
        data: { state: 'converted', converted_user_id: created.user_id ?? created.id },
        previousData: { id: signupRequestId, state: 'pending' } as RaRecord,
      });
      notify('resources.signupRequests.notifications.converted', { type: 'info' });
    } catch (error) {
      // The client exists either way. Saying that the tray is out of step is
      // more useful than reporting the whole operation as failed.
      notify(
        isAlreadyDecided(error)
          ? 'resources.signupRequests.notifications.already_decided'
          : 'resources.signupRequests.notifications.convert_orphan',
        { type: 'warning' }
      );
    }
    redirect('list', 'signupRequests');
  };

  return (
    <Create
      redirect="list"
      mutationOptions={signupRequestId ? { onSuccess: finishConversion } : undefined}
    >
      {/* Converting arrives with every field already filled in, so the form is
          untouched and the default pristine guard would refuse to save it. */}
      <SimpleForm
        toolbar={<MyToolbar resource="clients" alwaysEnableSave={Boolean(signupRequestId)} />}
      >
        <MyFormContainer>
          <ClientFormFields isCreate />
        </MyFormContainer>
      </SimpleForm>
    </Create>
  );
};

export default ClientCreate;
